// client/src/components/pages/Settings/LexiStarDataExport/LexiStarDataExport.tsx
//
// Settings > Monitoring > Data export (ST-00207, m4b contracts §4.3; T10-S05), route
// /settings/modules/monitoring/lexistar-export, permission lexistar.export.manage.
//
//   Request   a date range (the shared picker; it applies to events, messages, message costs
//             and positions — everything else is exported in full) and a dataset checklist
//             (grouped; positions only over 31 days or fewer; datasets the server lacks are
//             greyed out). A POPIA note shows while personal data is ticked.
//   Exports   the organisation's jobs: state with progress (polled while one is queued or
//             running), dates, datasets, size, how long the file is kept, and Download /
//             Cancel / Delete file. Download fetches a fresh one-time link
//             (GET /:id/download?redirect=false) and hands it to the browser, so the ZIP
//             streams straight to disk.
//
// Server: server/modules/lexistar/exports. Help: content/help/settings/export-your-data.md.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FaDownload, FaFileArchive, FaSyncAlt, FaTimes, FaTrash, FaUserShield } from 'react-icons/fa';
import { PageHeader, UnifiedDateRangePicker, useAuth } from '../../../../types/commonComponents';
import {
  absoluteUrl,
  cancelExport,
  DEFAULT_LIMITS,
  downloadLink,
  errorText,
  listExports,
  requestExport,
  startDownload,
  type DatasetInfo,
  type DatasetKey,
  type ExportJob,
  type ExportLimits,
} from './exportApi';
import {
  DATASET_GROUPS,
  daysInclusive,
  defaultRange,
  FALLBACK_DATASETS,
  fmtBytes,
  fmtCount,
  fmtRange,
  fmtWhen,
  isActive,
  labelOf,
  positionsBlockedReason,
  progressRatio,
  progressText,
  requestBody,
  scopeSummary,
  STATE_META,
  timeLeft,
} from './exportLogic';

export const PERMISSION = 'lexistar.export.manage';
export const POLL_MS = 4000;

const card = 'bg-surface rounded-xl border border-slate-200 shadow-sm';
const buttonBase =
  'inline-flex items-center justify-center gap-2 min-h-[40px] px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40';
const secondaryButton = `${buttonBase} text-slate-700 bg-surface border border-slate-200 hover:bg-slate-50`;
const primaryButton = `${buttonBase} px-4 font-semibold text-white bg-primary-500 hover:bg-primary-600 shadow-sm`;

type Tone = 'error' | 'notice';

function Banner({ tone, children, onDismiss }: { tone: Tone; children: ReactNode; onDismiss: () => void }) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`px-4 py-3 rounded-lg flex items-start gap-3 text-sm ${
        tone === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'
      }`}
    >
      <div className="flex-1 space-y-1">{children}</div>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="opacity-60 hover:opacity-100 p-1">
        <FaTimes size={12} />
      </button>
    </div>
  );
}

function StateChip({ job }: { job: ExportJob }) {
  const meta = STATE_META[job.state] ?? STATE_META.queued;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.className}`} data-state={job.state}>
      {meta.label}
    </span>
  );
}

function Progress({ job }: { job: ExportJob }) {
  const ratio = progressRatio(job);
  const text = progressText(job);
  if (!isActive(job)) return null;
  return (
    <div className="mt-1.5 min-w-[10rem]">
      <div
        className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden"
        role="progressbar"
        aria-label="Export progress"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(ratio !== null ? { 'aria-valuenow': Math.round(ratio * 100) } : {})}
      >
        <div
          className={`h-full rounded-full bg-primary-500 ${ratio === null ? 'w-1/3 animate-pulse' : ''}`}
          style={ratio !== null ? { width: `${Math.max(4, Math.round(ratio * 100))}%` } : undefined}
        />
      </div>
      <p className="mt-1 text-xs text-slate-500">{text}</p>
    </div>
  );
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div role="alertdialog" aria-modal="true" aria-labelledby="export-confirm-title" className="bg-surface rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-6">
          <h3 id="export-confirm-title" className="text-lg font-bold text-slate-900 mb-2">
            {title}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button type="button" onClick={onCancel} className={secondaryButton}>
            Keep it
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`${buttonBase} font-semibold text-white bg-red-500 hover:bg-red-600`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DatasetChecklist({
  catalogue,
  selected,
  onToggle,
  positionsBlocked,
}: {
  catalogue: readonly DatasetInfo[];
  selected: ReadonlySet<DatasetKey>;
  onToggle: (key: DatasetKey, on: boolean) => void;
  positionsBlocked: string | null;
}) {
  const byKey = new Map(catalogue.map((d) => [d.key, d]));
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {DATASET_GROUPS.map((group) => (
        <fieldset key={group.id} className="min-w-0">
          <legend className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{group.label}</legend>
          <ul className="space-y-1">
            {group.keys.map((key) => {
              const d = byKey.get(key) ?? FALLBACK_DATASETS.find((x) => x.key === key)!;
              const blocked = key === 'positions' ? positionsBlocked : null;
              const disabled = !d.available || !!blocked;
              const hintId = `export-ds-${key}-hint`;
              return (
                <li key={key}>
                  <label
                    className={`flex items-start gap-3 rounded-lg px-2 py-2 min-h-[44px] ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      checked={selected.has(key) && !disabled}
                      disabled={disabled}
                      onChange={(e) => onToggle(key, e.target.checked)}
                      aria-describedby={hintId}
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-800">
                        {d.label}
                        {d.dated && <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">date range</span>}
                        {d.personal && <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">personal data</span>}
                      </span>
                      <span id={hintId} className="block text-xs text-slate-500">
                        {!d.available ? 'Not available on this server yet.' : blocked ?? d.description}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}
    </div>
  );
}

function JobActions({
  job,
  busy,
  onDownload,
  onCancel,
  onDelete,
}: {
  job: ExportJob;
  busy: boolean;
  onDownload: (job: ExportJob) => void;
  onCancel: (job: ExportJob) => void;
  onDelete: (job: ExportJob) => void;
}) {
  const period = fmtRange(job.from_date, job.to_date);
  return (
    <div className="flex flex-wrap items-center gap-2 justify-end">
      {job.downloadable && (
        <>
          <button type="button" className={primaryButton} disabled={busy} onClick={() => onDownload(job)} aria-label={`Download the export of ${period}`}>
            <FaDownload size={12} /> Download
          </button>
          <button type="button" className={secondaryButton} disabled={busy} onClick={() => onDelete(job)} aria-label={`Delete the file of the export of ${period}`}>
            <FaTrash size={12} />
            <span className="sr-only sm:not-sr-only">Delete file</span>
          </button>
        </>
      )}
      {isActive(job) && (
        <button type="button" className={secondaryButton} disabled={busy} onClick={() => onCancel(job)} aria-label={`Cancel the export of ${period}`}>
          <FaTimes size={12} /> Cancel
        </button>
      )}
    </div>
  );
}

function jobDetail(job: ExportJob): ReactNode {
  if (job.state === 'failed') return <p className="mt-1 text-xs text-red-700 break-words">{job.error || 'The export failed.'}</p>;
  if (job.state === 'expired') return <p className="mt-1 text-xs text-slate-500">The file was deleted.</p>;
  if (job.state === 'done' && job.row_counts) {
    const rows = Object.values(job.row_counts).reduce((a, b) => a + (Number(b) || 0), 0);
    return <p className="mt-1 text-xs text-slate-500">{fmtCount(rows)} rows</p>;
  }
  return <Progress job={job} />;
}

const LexiStarDataExport = () => {
  const { hasPermission } = useAuth();
  const allowed = hasPermission(PERMISSION);

  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [catalogue, setCatalogue] = useState<readonly DatasetInfo[]>(FALLBACK_DATASETS);
  const [limits, setLimits] = useState<ExportLimits>(DEFAULT_LIMITS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ job: ExportJob; kind: 'cancel' | 'delete' } | null>(null);

  const initialRange = useMemo(() => defaultRange(DEFAULT_LIMITS.default_days), []);
  const [start, setStart] = useState<Date>(initialRange.start);
  const [end, setEnd] = useState<Date>(initialRange.end);
  const [selected, setSelected] = useState<Set<DatasetKey>>(() => new Set(FALLBACK_DATASETS.map((d) => d.key)));
  const seededSelection = useRef(false);

  const days = daysInclusive(start, end);
  const positionsBlocked = positionsBlockedReason(days, limits);
  const selectable = useCallback(
    (key: DatasetKey) => {
      const d = catalogue.find((x) => x.key === key);
      return !!d && d.available && !(key === 'positions' && positionsBlocked);
    },
    [catalogue, positionsBlocked]
  );
  const chosen = useMemo(() => new Set([...selected].filter(selectable)), [selected, selectable]);
  const active = jobs.find(isActive) ?? null;
  const body = requestBody(start, end, chosen);
  const personalChosen = catalogue.filter((d) => d.personal && chosen.has(d.key));

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const list = await listExports();
      setJobs(list.jobs);
      if (list.datasets.length) setCatalogue(list.datasets);
      setLimits(list.limits);
      if (!seededSelection.current && list.datasets.length) {
        seededSelection.current = true;
        setSelected(new Set(list.datasets.filter((d) => d.available).map((d) => d.key)));
      }
      if (!quiet) setError(null);
    } catch (err) {
      setError(errorText(err, 'The exports could not be loaded.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) void load();
    else setLoading(false);
  }, [allowed, load]);

  // Poll while an export is queued or running.
  const polling = jobs.some(isActive);
  useEffect(() => {
    if (!polling) return undefined;
    const t = setInterval(() => void load(true), POLL_MS);
    return () => clearInterval(t);
  }, [polling, load]);

  const toggle = (key: DatasetKey, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const selectAll = () => setSelected(new Set(catalogue.filter((d) => d.available).map((d) => d.key)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const out = await requestExport(body);
      setNotice(
        <>
          <p>Export requested. It is being prepared now; this page updates by itself.</p>
          {out.notes.map((n) => (
            <p key={n}>{n}</p>
          ))}
        </>
      );
      await load(true);
    } catch (err) {
      setError(errorText(err, 'The export could not be requested.'));
    } finally {
      setBusy(false);
    }
  };

  const download = async (job: ExportJob) => {
    setBusy(true);
    setError(null);
    try {
      const link = await downloadLink(job.id);
      startDownload(absoluteUrl(link.url), link.filename);
      setNotice(<p>Your download has started. A download link works once: to download again, click Download again.</p>);
      void load(true);
    } catch (err) {
      setError(errorText(err, 'The download could not be started.'));
      void load(true);
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    setError(null);
    try {
      await cancelExport(confirm.job.id);
      setNotice(<p>{confirm.kind === 'cancel' ? 'The export was cancelled.' : 'The file was deleted.'}</p>);
      setConfirm(null);
      await load(true);
    } catch (err) {
      setError(errorText(err, confirm.kind === 'cancel' ? 'The export could not be cancelled.' : 'The file could not be deleted.'));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <PageHeader
      icon={FaFileArchive}
      title="Data export"
      subtitle="Download your organisation's LexiStar data as CSV files in one ZIP file."
      actions={
        allowed ? (
          <button type="button" className={secondaryButton} onClick={() => void load()} disabled={loading} aria-label="Refresh the exports">
            <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        ) : undefined
      }
    />
  );

  if (!allowed) {
    return (
      <>
        {header}
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
          <div className={`${card} p-6 text-sm text-slate-600`} role="status">
            You need the permission <strong>Export LexiStar Data</strong> to export your organisation&apos;s data. Ask an administrator.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6 space-y-6">
        {error && (
          <Banner tone="error" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}
        {notice && (
          <Banner tone="notice" onDismiss={() => setNotice(null)}>
            {notice}
          </Banner>
        )}

        <section aria-labelledby="export-request-title" className={`${card} p-4 sm:p-6`}>
          <h2 id="export-request-title" className="text-base font-bold text-slate-900">
            Request an export
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Choose the dates and the data. You get one ZIP file with a CSV file for each dataset and a <code className="text-xs">manifest.json</code>{' '}
            that lists what is inside. The file is kept for {limits.retention_days} days.
          </p>

          <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
            <div>
              <span id="export-range-label" className="block text-sm font-semibold text-slate-800 mb-1.5">
                Date range
              </span>
              <div role="group" aria-labelledby="export-range-label">
                <UnifiedDateRangePicker
                  startDate={start}
                  endDate={end}
                  onStartDateChange={(d) => setStart(d)}
                  onEndDateChange={(d) => setEnd(d)}
                  bounds="past"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {days} day{days === 1 ? '' : 's'}. The dates apply to events, messages, message costs and positions. Everything else is exported in full.
              </p>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-slate-800">
                  Data <span className="font-normal text-slate-500">({chosen.size} chosen)</span>
                </span>
                <div className="flex gap-2">
                  <button type="button" className={secondaryButton} onClick={selectAll}>
                    Select all
                  </button>
                  <button type="button" className={secondaryButton} onClick={clearAll}>
                    Clear
                  </button>
                </div>
              </div>
              <DatasetChecklist catalogue={catalogue} selected={selected} onToggle={toggle} positionsBlocked={positionsBlocked} />
            </div>
          </div>

          {personalChosen.length > 0 && (
            <div className="mt-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="note">
              <FaUserShield className="mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p>
                This export holds personal information of your drivers ({personalChosen.map((d) => d.label.toLowerCase()).join(', ')}): names, phone numbers,
                Telegram chats and message texts. Under POPIA your organisation is responsible for it. Keep the file safe, share it only with people who
                need it, and delete copies you no longer need.
              </p>
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <button type="button" className={primaryButton} onClick={() => void submit()} disabled={busy || loading || !!active || chosen.size === 0}>
              <FaFileArchive size={13} /> Request export
            </button>
            <p className="text-xs text-slate-500" aria-live="polite">
              {active
                ? 'An export is already being prepared. Wait for it to finish, or cancel it below.'
                : chosen.size === 0
                  ? 'Choose at least one dataset.'
                  : `${scopeSummary(body.scope, catalogue)} from ${fmtRange(body.from_date, body.to_date)}.`}
            </p>
          </div>
        </section>

        <section aria-labelledby="export-jobs-title" className={card}>
          <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3">
            <h2 id="export-jobs-title" className="text-base font-bold text-slate-900">
              Your exports
            </h2>
            <p className="mt-1 text-sm text-slate-600">Each download link works once. Files are deleted {limits.retention_days} days after they are ready.</p>
          </div>

          {loading && jobs.length === 0 ? (
            <p className="px-4 sm:px-6 pb-6 text-sm text-slate-500">Loading…</p>
          ) : jobs.length === 0 ? (
            <p className="px-4 sm:px-6 pb-6 text-sm text-slate-500">No exports yet. Request one above.</p>
          ) : (
            <>
              {/* Phone: one card per export */}
              <ul className="md:hidden divide-y divide-slate-100 border-t border-slate-100" aria-label="Exports">
                {jobs.map((job) => (
                  <li key={job.id} className="px-4 py-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{fmtRange(job.from_date, job.to_date)}</p>
                        <p className="text-xs text-slate-500">
                          {scopeSummary(job.scope, catalogue)} · requested {fmtWhen(job.requested_at)}
                        </p>
                      </div>
                      <StateChip job={job} />
                    </div>
                    {jobDetail(job)}
                    {job.downloadable && (
                      <p className="text-xs text-slate-500">
                        {fmtBytes(job.size_bytes)} · kept until {fmtWhen(job.expires_at)} ({timeLeft(job.expires_at)})
                      </p>
                    )}
                    <JobActions
                      job={job}
                      busy={busy}
                      onDownload={(j) => void download(j)}
                      onCancel={(j) => setConfirm({ job: j, kind: 'cancel' })}
                      onDelete={(j) => setConfirm({ job: j, kind: 'delete' })}
                    />
                  </li>
                ))}
              </ul>

              {/* Tablet and desktop: a table */}
              <div className="hidden md:block overflow-x-auto border-t border-slate-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                      <th scope="col" className="px-4 py-2.5">Requested</th>
                      <th scope="col" className="px-4 py-2.5">Dates</th>
                      <th scope="col" className="px-4 py-2.5">Data</th>
                      <th scope="col" className="px-4 py-2.5">Status</th>
                      <th scope="col" className="px-4 py-2.5">Size</th>
                      <th scope="col" className="px-4 py-2.5">Kept until</th>
                      <th scope="col" className="px-4 py-2.5 text-right">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {jobs.map((job) => (
                      <tr key={job.id} className="align-top">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-slate-800">{fmtWhen(job.requested_at)}</span>
                          <span className="block text-xs text-slate-500 truncate max-w-[12rem]" title={job.requested_by}>
                            {job.requested_by}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{fmtRange(job.from_date, job.to_date)}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <span title={job.scope.map((k) => labelOf(k, catalogue)).join(', ')}>{scopeSummary(job.scope, catalogue)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <StateChip job={job} />
                          {jobDetail(job)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap tabular-nums text-slate-700">{job.state === 'done' ? fmtBytes(job.size_bytes) : '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                          {job.downloadable ? (
                            <>
                              {fmtWhen(job.expires_at)}
                              <span className="block text-xs text-slate-500">{timeLeft(job.expires_at)}</span>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <JobActions
                            job={job}
                            busy={busy}
                            onDownload={(j) => void download(j)}
                            onCancel={(j) => setConfirm({ job: j, kind: 'cancel' })}
                            onDelete={(j) => setConfirm({ job: j, kind: 'delete' })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>

      {confirm && (
        <ConfirmDialog
          title={confirm.kind === 'cancel' ? 'Cancel this export?' : 'Delete this file?'}
          message={
            confirm.kind === 'cancel'
              ? 'The export stops and nothing is kept. You can request a new one at any time.'
              : `The ZIP file is deleted now instead of after its ${limits.retention_days} days. Download links stop working. The request stays in the list.`
          }
          confirmLabel={confirm.kind === 'cancel' ? 'Cancel export' : 'Delete file'}
          busy={busy}
          onConfirm={() => void runConfirm()}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
};

export default LexiStarDataExport;
